/**
 * DifferentialPanel — Panel de razonamiento diferencial bayesiano
 * Las barras de probabilidad se mueven en tiempo real cuando el alumno explora nodos
 */

import { DifferentialConfig, DifferentialDx, DIFFERENTIAL_CASES } from './differential-data';
import state from '../core/state';

interface ExploredFinding {
  nodeId: string;
  label: string;
  impacts: Array<{ dxName: string; delta: number }>;
}

export class DifferentialPanel {
  private container: HTMLElement | null = null;
  private config: DifferentialConfig | null = null;
  private probabilities: Map<string, number> = new Map();
  private exploredFindings: ExploredFinding[] = [];
  private exploredNodeIds: Set<string> = new Set();
  private unsubscribers: Array<() => void> = [];
  private isConfirmed = false;
  private onDiagnosisConfirmed?: (dxName: string, isCorrect: boolean) => void;
  private lastLeaderName: string | null = null;

  constructor() {
    this.injectStyles();
  }

  start(caseId: string, onConfirmed?: (dxName: string, isCorrect: boolean) => void): void {
    this.config = DIFFERENTIAL_CASES[caseId];
    if (!this.config) return;

    this.onDiagnosisConfirmed = onConfirmed;
    this.isConfirmed = false;
    this.exploredFindings = [];
    this.exploredNodeIds.clear();
    this.lastLeaderName = null;

    // Inicializar probabilidades
    this.probabilities.clear();
    this.config.differentials.forEach(dx => {
      this.probabilities.set(dx.name, dx.initialProbability);
    });

    state.setEducationMode('quiz');
    this.render();

    // Hallazgos ya revelados antes de abrir el panel (ej: waves autoReveal)
    state.revealedFindings.forEach(id => this.onNodeExplored(id));

    // Escucha ambos canales: click directo (NODE_SELECT) y revelación por
    // examen o timeline (FINDING_REVEALED). Si un hallazgo se revela por
    // examen, la probabilidad se reajusta sin exigir click del alumno.
    this.unsubscribers.push(
      state.onEvent('NODE_SELECT', (e) => {
        if (e.type === 'NODE_SELECT' && !this.isConfirmed) this.onNodeExplored(e.nodeId);
      })
    );
    this.unsubscribers.push(
      state.onEvent('FINDING_REVEALED', (e) => {
        if (e.type === 'FINDING_REVEALED' && !this.isConfirmed) this.onNodeExplored(e.nodeId);
      })
    );
  }

  stop(): void {
    this.unsubscribers.forEach(fn => fn());
    this.unsubscribers = [];
    this.container?.remove();
    this.container = null;
    this.config = null;
    state.setEducationMode('none');
  }

  private onNodeExplored(nodeId: string): void {
    if (!this.config || this.exploredNodeIds.has(nodeId)) return;
    this.exploredNodeIds.add(nodeId);

    // Buscar el label del nodo en el state
    const patient = state.currentPatient;
    const nodeData = patient?.nodes.find(n => n.id === nodeId);
    if (!nodeData) return;

    const label = nodeData.concept.display;
    let valueStr = '';
    if (nodeData.valueQuantity) {
      valueStr = ` (${nodeData.valueQuantity.value} ${nodeData.valueQuantity.unit})`;
    } else if (nodeData.valueString) {
      valueStr = ` — ${nodeData.valueString}`;
    }

    // Calcular impactos
    const impacts: ExploredFinding['impacts'] = [];

    this.config.differentials.forEach(dx => {
      const delta = dx.findings[nodeId];
      if (delta !== undefined && delta !== 0) {
        const current = this.probabilities.get(dx.name) || 0;
        const newProb = Math.max(1, Math.min(95, current + delta));
        this.probabilities.set(dx.name, newProb);
        impacts.push({ dxName: dx.name, delta });
      }
    });

    // Normalizar probabilidades para que sumen ~100
    this.normalizeProbabilities();

    if (impacts.length > 0) {
      this.exploredFindings.push({ nodeId, label: label + valueStr, impacts });
    }

    this.publishLeadingHypothesis();
    this.updateUI();
  }

  /**
   * Identifica el diagnóstico líder actual y, si cambió respecto al anterior,
   * lo publica en el bus como hipótesis NO confirmada. Otros módulos
   * (física, HUD, AI panel) pueden reaccionar a este cambio.
   */
  private publishLeadingHypothesis(): void {
    if (!this.config) return;
    let leaderName = '';
    let leaderProb = -1;
    this.config.differentials.forEach(dx => {
      const p = this.probabilities.get(dx.name) ?? 0;
      if (p > leaderProb) { leaderProb = p; leaderName = dx.name; }
    });
    if (!leaderName || leaderName === this.lastLeaderName) return;
    this.lastLeaderName = leaderName;
    const dx = this.config.differentials.find(d => d.name === leaderName);
    if (dx) {
      state.setHypothesis(this.config.caseId, dx.name, dx.code, leaderProb, false);
    }
  }

  private normalizeProbabilities(): void {
    const total = Array.from(this.probabilities.values()).reduce((a, b) => a + b, 0);
    if (total > 0 && total !== 100) {
      const factor = 100 / total;
      this.probabilities.forEach((val, key) => {
        this.probabilities.set(key, Math.round(val * factor));
      });
    }
  }

  private confirmDiagnosis(dxName: string): void {
    if (!this.config) return;
    this.isConfirmed = true;

    const isCorrect = this.config.differentials[0].name === dxName;
    const correctDx = this.config.differentials[0];
    const chosen = this.config.differentials.find(d => d.name === dxName);

    // Publica hipótesis confirmada: futuros listeners (física, scoring) pueden
    // distinguir entre "liderando" y "elegida por el alumno".
    if (chosen) {
      const prob = this.probabilities.get(dxName) ?? 0;
      state.setHypothesis(this.config.caseId, chosen.name, chosen.code, prob, true);
    }

    // Marca que el alumno propuso un dx (correcto o no). Desbloquea el tab
    // "Diagnóstico" del Asistente IA, que antes bloqueaba para evitar que la
    // IA diera la respuesta antes del intento formativo.
    state.userProposedDx = { name: dxName, correct: isCorrect, at: Date.now() };

    this.onDiagnosisConfirmed?.(dxName, isCorrect);

    // Mostrar resultado
    this.showResult(dxName, isCorrect, correctDx);
  }

  private showResult(chosen: string, isCorrect: boolean, correctDx: DifferentialDx): void {
    if (!this.container) return;

    const resultEl = this.container.querySelector('#diff-result');
    if (!resultEl) return;

    resultEl.innerHTML = `
      <div class="diff-result-card ${isCorrect ? 'correct' : 'wrong'}">
        <div class="diff-result-icon">${isCorrect ? '&#x2705;' : '&#x274C;'}</div>
        <div class="diff-result-text">
          ${isCorrect
            ? '<strong>Correcto.</strong> Tu razonamiento diferencial fue acertado.'
            : `<strong>Revisa tu razonamiento.</strong> El diagnóstico más probable es <em>${correctDx.name}</em>.`
          }
        </div>
        <div class="diff-pearl">
          <span class="diff-pearl-icon">&#x1F4A1;</span>
          <span>${correctDx.clinicalPearl}</span>
        </div>
        <button class="diff-btn-close" id="diff-close">Continuar explorando</button>
      </div>
    `;

    resultEl.querySelector('#diff-close')?.addEventListener('click', () => this.stop());
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  private render(): void {
    this.container?.remove();
    this.container = document.createElement('div');
    this.container.id = 'diff-panel';
    document.body.appendChild(this.container);
    this.updateUI();
  }

  private updateUI(): void {
    if (!this.container || !this.config) return;

    const sorted = [...this.config.differentials].sort((a, b) =>
      (this.probabilities.get(b.name) || 0) - (this.probabilities.get(a.name) || 0)
    );

    const exploredCount = this.exploredFindings.length;

    this.container.innerHTML = `
      <div class="diff-header">
        <h3>Diagnóstico Diferencial</h3>
        <span class="diff-explored">${exploredCount} hallazgos explorados</span>
      </div>

      <div class="diff-bars">
        ${sorted.map(dx => {
          const prob = this.probabilities.get(dx.name) || 0;
          const isLeading = prob === Math.max(...Array.from(this.probabilities.values()));
          return `
            <div class="diff-dx ${isLeading ? 'leading' : ''}">
              <div class="diff-dx-header">
                <span class="diff-dx-name">${dx.name}</span>
                <span class="diff-dx-prob" style="color: ${dx.color}">${prob}%</span>
              </div>
              <div class="diff-bar-track">
                <div class="diff-bar-fill" style="width: ${prob}%; background: ${dx.color}; opacity: ${0.4 + prob * 0.006}"></div>
              </div>
            </div>
          `;
        }).join('')}
      </div>

      ${exploredCount > 0 ? `
        <div class="diff-findings">
          <div class="diff-findings-title">Hallazgos</div>
          ${this.exploredFindings.slice(-5).map(f => `
            <div class="diff-finding">
              <span class="diff-finding-label">${f.label}</span>
              <div class="diff-finding-impacts">
                ${f.impacts.map(imp => `
                  <span class="diff-impact ${imp.delta > 0 ? 'pos' : 'neg'}">
                    ${imp.delta > 0 ? '+' : ''}${imp.delta} ${imp.dxName.split(' ')[0]}
                  </span>
                `).join('')}
              </div>
            </div>
          `).join('')}
        </div>
      ` : `
        <div class="diff-hint">
          <span>&#x1F50D;</span> Haz clic en los nodos clínicos para explorar hallazgos.<br>
          Las probabilidades se ajustarán en tiempo real.
        </div>
      `}

      <div id="diff-result"></div>

      ${!this.isConfirmed && exploredCount >= 2 ? `
        <div class="diff-confirm">
          <div class="diff-confirm-label">¿Cuál es tu diagnóstico?</div>
          <div class="diff-confirm-options">
            ${sorted.slice(0, 4).map(dx => `
              <button class="diff-confirm-btn" data-dx="${dx.name}" style="border-color: ${dx.color}40; color: ${dx.color}">
                ${dx.name}
              </button>
            `).join('')}
          </div>
        </div>
      ` : ''}
    `;

    // Event listeners para confirmar diagnóstico
    this.container.querySelectorAll('.diff-confirm-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const name = (e.target as HTMLElement).dataset.dx!;
        this.confirmDiagnosis(name);
      });
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ESTILOS
  // ═══════════════════════════════════════════════════════════════════════

  private injectStyles(): void {
    if (document.getElementById('diff-styles')) return;
    const s = document.createElement('style');
    s.id = 'diff-styles';
    s.textContent = `
      #diff-panel {
        position: fixed;
        left: 20px; top: 90px; bottom: 20px;
        width: 300px;
        background: rgba(10,10,15,0.58);
        backdrop-filter: blur(28px) saturate(140%); -webkit-backdrop-filter: blur(28px) saturate(140%);
        border: 0.5px solid rgba(255,255,255,0.08);
        border-radius: 16px;
        padding: 20px;
        z-index: 200;
        overflow-y: auto;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        color: #f5f5f7;
        display: flex;
        flex-direction: column;
        gap: 16px;
        animation: diffIn 0.4s cubic-bezier(0.25,1,0.5,1);
      }
      #diff-panel::-webkit-scrollbar { width: 4px; }
      #diff-panel::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }

      .diff-header {
        display: flex; align-items: center; justify-content: space-between;
      }
      .diff-header h3 {
        font-size: 15px; font-weight: 700; letter-spacing: -0.3px; margin: 0;
      }
      .diff-explored {
        font-size: 11px; color: #6e6e73;
        font-family: 'JetBrains Mono', monospace;
      }

      .diff-bars { display: flex; flex-direction: column; gap: 10px; }
      .diff-dx { transition: all 0.3s; }
      .diff-dx.leading { transform: scale(1.02); }
      .diff-dx-header {
        display: flex; justify-content: space-between; align-items: center;
        margin-bottom: 4px;
      }
      .diff-dx-name { font-size: 12px; font-weight: 600; color: #a1a1a6; }
      .diff-dx.leading .diff-dx-name { color: #f5f5f7; }
      .diff-dx-prob {
        font-family: 'JetBrains Mono', monospace;
        font-size: 13px; font-weight: 700;
      }
      .diff-bar-track {
        height: 6px; background: rgba(255,255,255,0.05);
        border-radius: 3px; overflow: hidden;
      }
      .diff-bar-fill {
        height: 100%; border-radius: 3px;
        transition: width 0.6s cubic-bezier(0.25,1,0.5,1), opacity 0.6s;
      }

      .diff-findings { border-top: 0.5px solid rgba(255,255,255,0.06); padding-top: 12px; }
      .diff-findings-title {
        font-size: 10px; font-weight: 600; text-transform: uppercase;
        letter-spacing: 1px; color: #6e6e73; margin-bottom: 8px;
      }
      .diff-finding {
        padding: 8px 10px; border-radius: 8px;
        background: rgba(255,255,255,0.03);
        margin-bottom: 6px;
        animation: diffIn 0.3s ease;
      }
      .diff-finding-label {
        font-size: 12px; font-weight: 500; color: #f5f5f7;
        display: block; margin-bottom: 4px;
      }
      .diff-finding-impacts { display: flex; flex-wrap: wrap; gap: 4px; }
      .diff-impact {
        font-family: 'JetBrains Mono', monospace;
        font-size: 9px; font-weight: 600;
        padding: 2px 6px; border-radius: 4px;
      }
      .diff-impact.pos { background: rgba(48,209,88,0.12); color: #4ADE80; }
      .diff-impact.neg { background: rgba(255,69,58,0.12); color: #FF6961; }

      .diff-hint {
        text-align: center; padding: 20px 12px;
        font-size: 13px; color: #6e6e73; line-height: 1.6;
      }
      .diff-hint span { font-size: 20px; display: block; margin-bottom: 8px; }

      .diff-confirm {
        border-top: 0.5px solid rgba(255,255,255,0.06);
        padding-top: 14px;
      }
      .diff-confirm-label {
        font-size: 13px; font-weight: 600; margin-bottom: 10px; color: #f5f5f7;
      }
      .diff-confirm-options { display: flex; flex-direction: column; gap: 6px; }
      .diff-confirm-btn {
        padding: 10px 14px; border-radius: 10px;
        background: rgba(255,255,255,0.03);
        border: 1px solid; cursor: pointer;
        font-size: 12px; font-weight: 600;
        text-align: left; transition: all 0.2s;
        font-family: inherit;
      }
      .diff-confirm-btn:hover {
        background: rgba(255,255,255,0.08);
        transform: translateX(4px);
      }

      .diff-result-card {
        padding: 16px; border-radius: 12px;
        animation: diffIn 0.3s ease;
      }
      .diff-result-card.correct {
        background: rgba(48,209,88,0.08);
        border: 0.5px solid rgba(48,209,88,0.2);
      }
      .diff-result-card.wrong {
        background: rgba(255,69,58,0.08);
        border: 0.5px solid rgba(255,69,58,0.2);
      }
      .diff-result-icon { font-size: 24px; margin-bottom: 8px; }
      .diff-result-text { font-size: 13px; color: #a1a1a6; line-height: 1.5; margin-bottom: 12px; }
      .diff-pearl {
        display: flex; gap: 8px; padding: 10px 12px;
        background: rgba(255,159,10,0.06); border-radius: 8px;
        font-size: 11px; color: #a1a1a6; line-height: 1.5;
        margin-bottom: 12px;
      }
      .diff-pearl-icon { flex-shrink: 0; }
      .diff-btn-close {
        width: 100%; padding: 10px; border-radius: 10px;
        background: rgba(255,255,255,0.06); border: 0.5px solid rgba(255,255,255,0.1);
        color: #f5f5f7; font-size: 13px; font-weight: 600;
        cursor: pointer; transition: all 0.2s; font-family: inherit;
      }
      .diff-btn-close:hover { background: rgba(255,255,255,0.12); }

      @keyframes diffIn {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(s);
  }
}

export const differentialPanel = new DifferentialPanel();
export default DifferentialPanel;
